import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCategoriesOpen, setMobileCategoriesOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Home', href: '/' },
    { label: 'Categories', href: '/#categories' },
    { label: 'Guides', href: '/#guides' },
    { label: 'Comparisons', href: '/#comparisons' },
    { label: 'Safety', href: '/#safety' },
  ];

  const articleLinks = [
    { label: 'Kakobuy Spreadsheet Guide', href: '/kakobuy-spreadsheet-guide' },
    { label: 'Sneakers Guide', href: '/kakobuy-spreadsheet-sneakers' },
    { label: 'CSSBuy Guide', href: '/cssbuy-spreadsheet-guide' },
    { label: 'Superbuy Guide', href: '/superbuy-spreadsheet-guide' },
    { label: 'Oopbuy Guide', href: '/oopbuy-spreadsheet-guide' },
    { label: 'Kakobuy vs CSSBuy', href: '/kakobuy-vs-cssbuy' },
    { label: 'Kakobuy vs Superbuy', href: '/kakobuy-vs-superbuy' },
    { label: 'Kakobuy vs Oopbuy', href: '/kakobuy-vs-oopbuy' },
    { label: 'Finds Database', href: '/kakobuy-finds-database' },
    { label: 'Safety Guide', href: '/kakobuy-safety-guide' },
    { label: 'Fashion Guide', href: '/kakobuy-spreadsheet-fashion' },
  ];

  const handleClose = () => {
    setMobileOpen(false);
    setMobileCategoriesOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-6 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link
            to="/"
            className="font-heading text-xl md:text-2xl font-bold text-foreground-900 tracking-tight whitespace-nowrap"
          >
            Kakobuy<span className="text-primary-600">Finds</span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap"
              >
                {link.label}
              </a>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              kakobuy sheet
              <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
            </a>
            <button
              onClick={() => {
                setMobileOpen(!mobileOpen);
                if (mobileOpen) setMobileCategoriesOpen(false);
              }}
              className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-800 cursor-pointer"
              aria-label="Toggle menu"
            >
              <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 max-h-[80vh] overflow-y-auto">
          <div className="px-6 py-4 flex flex-col gap-1">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={handleClose}
                className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors py-2.5 px-3 rounded-lg hover:bg-background-100"
              >
                {link.label}
              </a>
            ))}

            <div className="border-t border-background-200 my-2"></div>

            <button
              onClick={() => setMobileCategoriesOpen(!mobileCategoriesOpen)}
              className="flex items-center justify-between text-sm font-semibold text-foreground-800 py-2.5 px-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer w-full"
            >
              <span className="flex items-center gap-2">
                <i className="ri-shopping-bag-3-line w-4 h-4 flex items-center justify-center"></i>
                Shop Categories
              </span>
              <i className={mobileCategoriesOpen ? 'ri-arrow-up-s-line w-4 h-4 flex items-center justify-center transition-transform' : 'ri-arrow-down-s-line w-4 h-4 flex items-center justify-center transition-transform'}></i>
            </button>

            {mobileCategoriesOpen && (
              <div className="ml-3 pl-3 border-l-2 border-background-200 flex flex-col gap-0.5">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={handleClose}
                    className="flex items-center gap-2 text-sm text-foreground-600 hover:text-foreground-900 transition-colors py-2 px-3 rounded-lg hover:bg-background-100 whitespace-nowrap"
                  >
                    <i className={`${cat.icon} w-4 h-4 flex items-center justify-center text-primary-500`}></i>
                    {cat.name}
                    <i className="ri-arrow-right-up-line w-3 h-3 flex items-center justify-center ml-auto text-foreground-400"></i>
                  </a>
                ))}
              </div>
            )}

            <div className="border-t border-background-200 my-2"></div>

            <Link
              to="/#guides"
              onClick={handleClose}
              className="flex items-center gap-2 text-sm font-semibold text-primary-700 py-2.5 px-3 rounded-lg hover:bg-primary-50 transition-colors cursor-pointer w-full"
            >
              <i className="ri-article-line w-4 h-4 flex items-center justify-center"></i>
              SEO Articles &amp; Guides
            </Link>

            {articleLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={handleClose}
                className="text-sm text-foreground-600 hover:text-foreground-900 transition-colors py-2 px-3 rounded-lg hover:bg-background-100 ml-3"
              >
                {link.label}
              </Link>
            ))}

            <div className="border-t border-background-200 my-2"></div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={handleClose}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer mt-1"
            >
              kakobuy sheet
              <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}